import type { PublicLinkPageProviderData } from '@confluence/public-links/entry-points/usePublicLinkPageDataProvider.ts';
import type { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import type { GroupOrTeamPrincipal, Principal } from '../../controlled/commonTypes.tsx';
import type { ContentRestrictionsQueryData, QueriesState } from '../Controller.tsx';
import type { ContentType } from '../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator.tsx';

import type { UseTeamDataResult } from './useShareDialogTeamDataHandler.ts';
import { getCurrentUserPrincipal } from './getCurrentUserPrincipal.ts';
import { getGeneralAccessLevel, getPrincipalsAndRoles } from './getContentRestrictions.ts';
import {
	canSetRestrictions,
	getClosestAncestorWithViewRestrictions,
} from './dataToQueriesState.ts';
import type {
	useQueriesShareAndRestrictDialogQuery_content_PaginatedContentListWithChild as ContentData,
	useQueriesShareAndRestrictDialogQuery_Query as QueryData,
} from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import { ConfluenceGroupUsageType } from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import type { GroupAndTeamCountsMapType } from './useGroupsCountQuery.ts';

type OperationType = 'read' | 'update';

// If you add any new fields to this, be sure to test that the USD / PublishModal integration still works.
export interface PublishModalUserInput {
	type: 'known' | 'unknown' | 'anonymous' | 'user';
	accountId: string;
	displayName: string;
	publicName: string;
	profilePicture: {
		path: string;
	};
	isExternalCollaborator?: boolean;
	email: string;
}

// If you add any new fields to this, be sure to test that the USD / PublishModal integration still works.
export interface PublishModalGroupInput {
	type: 'group';
	name: string;
	id: string;
	//TODO updates this once you update publish modal API
	usageType?: ConfluenceGroupUsageType;
}

export interface ContentRestriction {
	operation: OperationType;
	restrictions: {
		user: {
			results: PublishModalUserInput[];
		};
		group: {
			results: PublishModalGroupInput[];
		};
	};
}

export type PublishModalRestrictionsInput = ContentRestriction[];

type UsersAndGroups = {
	users: Principal[];
	groups: GroupOrTeamPrincipal[];
};

type ReadAndUpdate = {
	read: UsersAndGroups;
	update: UsersAndGroups;
};

export function publishModalDataToQueriesState({
	publishModalRestrictions,
	spaceKey,
	publicLinkPageData,
	data,
	contentType,
	edition,
	groupAndTeamCountsMap,
	teamData,
}: {
	publishModalRestrictions: PublishModalRestrictionsInput | undefined;
	publicLinkPageData: PublicLinkPageProviderData;
	spaceKey: string;
	data: QueryData;
	contentType: ContentType;
	edition: ConfluenceEdition;
	groupAndTeamCountsMap: GroupAndTeamCountsMapType;
	teamData: UseTeamDataResult['teamData'];
}): QueriesState | null {
	if (!publishModalRestrictions) return null;

	const currentUser = getCurrentUserPrincipal(data.user);
	if (!currentUser) return null;

	const contentData = getContentData(
		publishModalRestrictions,
		data.content,
		currentUser,
		groupAndTeamCountsMap,
		teamData,
	);
	if (!contentData) return null;

	const trialEndDate = data?.license?.trialEndDate || null;

	return {
		...contentData,
		spaceKey,
		currentUser,
		publicLinkPageData,
		userCanEditRestrictions: canSetRestrictions(
			data,
			contentData.contentRestrictions.generalAccessLevel,
			contentType,
		),
		edition,
		currentUserHasAccess: contentData.currentUserHasAccess,
		disableSharingToEmails: false,
		principalsPendingAccess: [],
		userCanEditContent: false,
		userCount: 0,
		trialEndDate,
	};
}

function getContentData(
	publishModalRestrictions: PublishModalRestrictionsInput,
	content: ContentData | null,
	currentUser: Principal,
	groupAndTeamCountsMap: GroupAndTeamCountsMapType,
	teamData: UseTeamDataResult['teamData'],
): Pick<
	QueriesState,
	| 'contentRestrictions'
	| 'hasInheritedRestrictions'
	| 'parentPageLink'
	| 'groupCount'
	| 'teamCount'
	| 'numberOfAncestors'
	| 'currentUserHasAccess'
> | null {
	if (!content) return null;

	const { nodes } = content;
	if (!nodes) return null;

	const [node0] = nodes;
	if (!node0) return null;

	const { hasInheritedRestrictions, ancestors } = node0;

	const contentRestrictionsAndCounts = getContentRestrictions(
		publishModalRestrictions,
		currentUser,
		groupAndTeamCountsMap,
		teamData,
	);

	const parentPageLink = hasInheritedRestrictions
		? getClosestAncestorWithViewRestrictions(ancestors)
		: null;

	return {
		contentRestrictions: {
			generalAccessLevel: contentRestrictionsAndCounts.generalAccessLevel,
			principals: contentRestrictionsAndCounts.principals,
		},
		numberOfAncestors: ancestors?.length || 0,
		groupCount: contentRestrictionsAndCounts.groupCount,
		teamCount: contentRestrictionsAndCounts.teamCount,
		currentUserHasAccess: contentRestrictionsAndCounts.currentUserHasAccess,
		hasInheritedRestrictions,
		parentPageLink,
	};
}

function getContentRestrictions(
	publishModalRestrictions: PublishModalRestrictionsInput,
	currentUser: Principal,
	groupAndTeamCountsMap: GroupAndTeamCountsMapType,
	teamData: UseTeamDataResult['teamData'],
): ContentRestrictionsQueryData & {
	groupCount: number;
	teamCount: number;
	currentUserHasAccess: boolean;
} {
	const readAndUpdateLists = convertPublishModalRestrictions(
		publishModalRestrictions,
		groupAndTeamCountsMap,
		teamData,
	);
	const { principals, groupCount, teamCount, currentUserHasAccess } = getPrincipalsAndRoles(
		readAndUpdateLists,
		currentUser,
	);

	return {
		generalAccessLevel: getGeneralAccessLevel(readAndUpdateLists),
		principals,
		groupCount,
		teamCount,
		currentUserHasAccess,
	};
}

function convertPublishModalRestrictions(
	publishModalRestrictions: PublishModalRestrictionsInput,
	groupAndTeamCountsMap: GroupAndTeamCountsMapType,
	teamData: UseTeamDataResult['teamData'],
) {
	const ret: ReadAndUpdate = {
		read: { users: [], groups: [] },
		update: { users: [], groups: [] },
	};

	const { read, update } = ret;

	for (const data of publishModalRestrictions) {
		const { operation } = data;

		if (operation === 'read') {
			convertAndPushUsers({
				users: data.restrictions.user.results,
				destination: read.users,
			});
			convertAndPushGroups({
				groups: data.restrictions.group.results,
				destination: read.groups,
				groupAndTeamCountsMap,
				teamData,
			});
		}

		if (operation === 'update') {
			convertAndPushUsers({
				users: data.restrictions.user.results,
				destination: update.users,
			});
			convertAndPushGroups({
				groups: data.restrictions.group.results,
				destination: update.groups,
				groupAndTeamCountsMap,
				teamData,
			});
		}
	}

	return ret;
}

function convertAndPushGroups({
	groups,
	destination,
	groupAndTeamCountsMap,
	teamData,
}: {
	groups: PublishModalGroupInput[];
	destination: GroupOrTeamPrincipal[];
	groupAndTeamCountsMap: GroupAndTeamCountsMapType;
	teamData: UseTeamDataResult['teamData'];
}) {
	for (const group of groups) {
		if (!group) continue;
		const id = group?.id || '';
		// Find the matching team in the teamsV2 array by comparing IDs
		// IDs may be in ARI format (e.g., "ari:cloud:teams::team/uuid"), so we compare the last segment
		const groupIdSegment =
			id.toString().lastIndexOf('/') >= 0
				? id.toString().substring(id.toString().lastIndexOf('/') + 1)
				: id;
		const teamItem =
			group?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION
				? teamData?.teamsV2?.find((team) => {
						const teamIdSegment =
							team?.id && team?.id?.toString().lastIndexOf('/') >= 0
								? team?.id?.toString().substring(team?.id?.toString().lastIndexOf('/') + 1)
								: team?.id;
						return teamIdSegment === groupIdSegment;
					})
				: undefined;
		destination.push({
			type: 'group',
			id: group.id,
			name: group.name,
			usageType: group.usageType || ConfluenceGroupUsageType.USERBASE_GROUP,
			memberCount: groupAndTeamCountsMap.get(group.id || '') ?? 0,
			isVerified: teamItem?.type?.verified ?? false,
			avatarSrc: teamItem?.largeAvatarImageUrl ?? '',
			teamTypeName: teamItem?.type?.name ?? undefined,
		});
	}
}

function convertAndPushUsers({
	users,
	destination,
}: {
	users: PublishModalUserInput[];
	destination: Principal[];
}) {
	for (const user of users) {
		if (!user) continue;
		destination.push({
			type: 'user',
			id: user.accountId,
			avatarUrl: user.profilePicture?.path || '',
			name: user.displayName,
			email: user.email,
			knownErrors: undefined,
			sitePermissionType: 'INTERNAL',
		});
	}
}
